import { useGetOpenCruciblesInfiniteQuery } from '@sorrem-client/ui/store';
import { resolveVeilSaturationById, type OpenCrucibleSummary, type VeilSaturationId } from '@sorrem/shared';
import { useCallback, useMemo } from 'react';

export interface CatalogCrucible {
    readonly id: string;
    readonly name: string;
    readonly veilSaturation: VeilSaturationId;
    readonly description: string;
    /** The readiness its saturation calls for (#206). */
    readonly recommendedReadiness: { readonly min: number; readonly max: number | null };
    /** Banner tint; stands in for real crucible artwork. */
    readonly accentColor: string;
    readonly maxPlayers: number;
    /** Fewest members the run starts with (#206). */
    readonly minPlayers: number;
    readonly memberCount: number;
}

interface CrucibleCatalogState {
    /** The crucibles loaded so far, a page at a time. */
    readonly crucibles: readonly CatalogCrucible[];
    /** Whether the server has more open crucibles than are loaded. */
    readonly hasMore: boolean;
    readonly isLoadingMore: boolean;
    /** Loads the next page onto the list. */
    readonly showMore: () => void;
}

/**
 * The `/characters` socket's `crucibles:changed` refreshes the list the
 * moment it changes (see `useCharacterUpdates`); this slow poll is a safety
 * net for a change the server didn't announce or a socket that dropped.
 */
const OPEN_CRUCIBLES_REFRESH_INTERVAL_MS = 30_000;

/**
 * Per-band description/color. `description` is not authored content yet -
 * real per-location flavor text is a separate content-design task, not
 * wiring. These are deliberately minimal, mechanically-derived placeholders
 * so the browse UI has something honest to show instead of the old
 * hardcoded mockup crucibles. Crucible affixes are left out until they are
 * defined.
 */
const SATURATION_FLAVOR: Record<VeilSaturationId, { description: string; accentColor: string }> = {
    faint: { description: 'Veil resonance is only faintly stirring here.', accentColor: '#4a5a7e' },
    stirring: { description: 'The Veil is stirring - resonance is building.', accentColor: '#5b3a9e' },
    charged: { description: 'This crucible is charged with condensed Veil resonance.', accentColor: '#a4501f' },
    overflowing: { description: 'Resonance is overflowing - this crucible is dangerously saturated.', accentColor: '#8f1f3a' },
};

/** The banner tint for a saturation band, standing in for crucible artwork. */
export function resolveSaturationAccent(id: VeilSaturationId): string {
    return SATURATION_FLAVOR[id].accentColor;
}

/** What veil-touched sense at a crucible's threshold (#225), or its saturation's general note. */
export function resolveCrucibleDescription(backstory: string | null, saturation: VeilSaturationId): string {
    return backstory ?? SATURATION_FLAVOR[saturation].description;
}

function toCatalogCrucible(summary: OpenCrucibleSummary): CatalogCrucible {
    const band = resolveVeilSaturationById(summary.veilSaturation);
    const flavor = SATURATION_FLAVOR[summary.veilSaturation];
    return {
        id: summary.id,
        name: summary.displayName,
        veilSaturation: summary.veilSaturation,
        description: resolveCrucibleDescription(summary.backstory, summary.veilSaturation),
        recommendedReadiness: band.recommendedReadiness,
        accentColor: flavor.accentColor,
        maxPlayers: summary.maxPlayers,
        minPlayers: summary.minPlayers,
        memberCount: summary.memberCount,
    };
}

/**
 * Returns the open crucibles a player can browse and join, a page at a time:
 * `showMore` loads the next page. A refresh reloads every page shown.
 */
export function useCrucibleCatalog(): CrucibleCatalogState {
    const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = useGetOpenCruciblesInfiniteQuery(undefined, {
        refetchOnMountOrArgChange: true,
        pollingInterval: OPEN_CRUCIBLES_REFRESH_INTERVAL_MS,
        // A hidden tab has nobody reading the list: it refreshes once the page is back in focus.
        skipPollingIfUnfocused: true,
    });
    const crucibles = useMemo(() => {
        // A crucible can move to the next page as others open or close between loads: shown once.
        const seenIds = new Set<string>();
        return (data?.pages ?? [])
            .flatMap((page) => page.data)
            .filter((summary) => !seenIds.has(summary.id) && seenIds.add(summary.id))
            .map(toCatalogCrucible);
    }, [data]);
    const showMore = useCallback((): void => {
        void fetchNextPage();
    }, [fetchNextPage]);
    return { crucibles, hasMore: hasNextPage, isLoadingMore: isFetchingNextPage, showMore };
}
